<script>
import TinyInvoice from "@/components/Invoice/TinyInvoice.vue";
import LoanPageEditableBox from "@/components/Loan/LoanPageEditableBox.vue";
import DonationSlider from "@/components/shared/DonationSlider.vue";
import IconButton from "@/components/shared/IconButton.vue";
import ProvisionModal from "@/components/User/ProvisionModal.vue";
import { debounce } from "@/helpers/debounce";
import { normalizeCurrency } from "@/helpers/filters";
import { summarizeBorrowerInvoice } from "@/helpers/invoices";
import { getOwner, isCoownerOrOwner } from "@/helpers/permissions/loanables";
import { isBorrower, isLoanAdmin } from "@/helpers/permissions/loans";
import { put } from "@/requests/server";
import { sections } from "@/components/Loan/FullLoanSections";
export default {
  name: "LoanPaymentBox",
  components: { LoanPageEditableBox, IconButton, DonationSlider, ProvisionModal, TinyInvoice },
  props: {
    loan: {
      type: Object,
      required: true,
    },
    borrowerInvoice: {
      type: Object,
      default: () => null,
    },
    desiredContribution: {
      type: [Number, String],
      default: 0,
    },
    ownerInvoice: {
      type: Object,
      default: () => null,
    },
    estimating: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isChangingDistance: {
      type: Boolean,
      default: false,
    },
    isChangingDuration: {
      type: Boolean,
      default: false,
    },
    sharedInvoice: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      platformTip:
        this.loan.applicable_amount_types.contributions === "not_applicable"
          ? 0
          : normalizeCurrency(this.loan.platform_tip),
      loading: false,
      sections,
    };
  },
  computed: {
    initiallyEditing() {
      if (!this.isBorrower) {
        return false;
      }

      if (!this.loanIsInProcess) {
        return false;
      }

      if (!this.loan.borrower_may_contribute) {
        return false;
      }

      if (this.loan.status === "validated" || this.loan.status === "accepted") {
        return true;
      }

      if (this.loan.platform_tip === null) {
        return true;
      }

      // Only contribution to show, might as well show the slider
      if (!this.loan.borrower_must_pay_insurance && !this.loan.borrower_must_pay_compensation) {
        return true;
      }

      // If user cannot pay initially, show slider
      return this.user.balance < -(this.loan.borrower_invoice?.user_balance_change ?? 0);
    },
    user() {
      return this.$store.state.user;
    },
    isBorrower() {
      return isBorrower(this.user, this.loan);
    },
    isLoanAdmin() {
      return isLoanAdmin(this.user, this.loan);
    },
    isCoownerOrOwner() {
      return isCoownerOrOwner(this.user, this.loan.loanable);
    },
    groupedSharedInvoice() {
      return {
        items: [
          {
            ...this.sharedInvoice,
            label: this.sharedInvoice.title,
          },
        ],
      };
    },
    groupedSummarizedOwnerInvoice() {
      if (this.isBorrower || this.isLoanAdmin || !this.isCoownerOrOwner) {
        return null;
      }

      return {
        items: [
          {
            ...this.ownerInvoice,
            label: this.$t("helpers.invoices.compensation.label_for_owner"),
          },
        ],
      };
    },
    actualPrice() {
      return -(this.borrowerInvoice?.user_balance_change ?? 0);
    },
    userHasSufficientBalance() {
      return this.user.balance >= this.actualPrice;
    },
    hasNoInvoiceItems() {
      return !this.borrowerInvoice || this.borrowerInvoice.items.length === 0;
    },
    actionLabel() {
      if (this.loan.status === "accepted") {
        return this.$t("components.loan.payment_box.action_labels.prepay");
      }
      if (this.loan.status === "validated") {
        if (this.hasNoInvoiceItems && this.platformTip === 0) {
          if (!this.isExemptFromContributions) {
            return this.$t("components.loan.payment_box.action_labels.finish_without_contributing");
          } else {
            return this.$t("components.loan.payment_box.action_labels.finish_loan");
          }
        }
        if (!this.loan.borrower_must_pay_insurance && !this.loan.borrower_must_pay_compensation) {
          return this.$t("components.loan.payment_box.action_labels.contribute");
        }
        return this.$t("components.loan.payment_box.action_labels.pay");
      }
      if (this.isBorrower && !this.isExemptFromContributions && !this.wouldSaveUnchangedTip) {
        if (this.platformTip === 0) {
          return this.$t("components.loan.payment_box.action_labels.continue_without_contribution");
        }
        return this.$t("components.loan.payment_box.action_labels.save_contribution");
      }
      return null;
    },
    paymentButtonLabel() {
      if (this.userHasSufficientBalance) {
        return this.actionLabel;
      }

      if (this.actionLabel) {
        return this.$t("components.loan.payment_box.action_labels.add_to_balance_and_action", {
          action: this.actionLabel,
        });
      }

      return this.$t("components.loan.payment_box.action_labels.add_to_balance");
    },
    summarizedBorrowerInvoiceItems() {
      if (!this.borrowerInvoice || (!this.isBorrower && !this.isLoanAdmin)) {
        return null;
      }

      return summarizeBorrowerInvoice(this.borrowerInvoice, {
        ownerName: getOwner(this.loan.loanable).name,
        communityName: this.loan.community.name,
        groupCompensations: true,
        contributionsDetails: true,
      }).items;
    },
    summarizedOwnerInvoice() {
      if (this.isBorrower || this.isLoanAdmin || !this.isCoownerOrOwner) {
        return null;
      }
      return this.ownerInvoice;
    },
    contributionTotal() {
      let total = 0;

      for (const item of this.borrowerInvoice?.items ?? []) {
        if (
          item.item_type === "donation.loan" ||
          item.item_type === "loan.contribution" ||
          item.item_type === "loan.community_contribution"
        ) {
          total += item.total;
        }
      }

      return total;
    },
    isInvoiceUpdating() {
      return (
        this.estimating ||
        this.contributionTotal !== -this.platformTip ||
        this.$refs.donationSlider?.isChanging
      );
    },
    isFree() {
      return this.loan.is_free;
    },
    loanIsInProcess() {
      return (
        this.loan.status !== "canceled" &&
        this.loan.status !== "rejected" &&
        this.loan.status !== "completed"
      );
    },
    isExemptFromContributions() {
      return this.loan.applicable_amount_types.contributions === "not_applicable";
    },
    wouldSaveUnchangedTip() {
      if (!this.loan.id) {
        return false;
      }

      return (
        this.loan.status !== "accepted" &&
        this.loan.status !== "validated" &&
        this.platformTip === this.loan.platform_tip
      );
    },
    wouldPayOrPrepayWithChangedInfo() {
      return (
        (this.loan.status === "accepted" || this.loan.status === "validated") &&
        (this.isChangingDuration || this.isChangingDistance)
      );
    },
  },
  watch: {
    initiallyEditing() {
      this.maybeSetPlatformTipToDesiredValue();
    },
    desiredContribution() {
      this.maybeSetPlatformTipToDesiredValue();
    },
  },
  mounted() {
    this.maybeSetPlatformTipToDesiredValue();
  },
  created() {
    this.debouncedEstimatePrice = debounce(this.estimatePrice, 150);
  },
  methods: {
    async onPlatformTipSaved(tip) {
      let endpoint = `/loans/${this.loan.id}/tip`;
      let action = this.$t("components.loan.payment_box.notifications.contribution_modified");
      let onSuccess = this.$t("components.loan.payment_box.notifications.contribution_saved");

      if (this.loan.status === "accepted") {
        endpoint = `/loans/${this.loan.id}/prepay`;
        action = this.$t("components.loan.payment_box.notifications.prepaid");
        onSuccess = this.$t("components.loan.payment_box.notifications.loan_prepaid");
      } else if (this.loan.status === "validated") {
        endpoint = `/loans/${this.loan.id}/pay`;
        action = this.$t("components.loan.payment_box.notifications.payment");
        onSuccess = this.$t("components.loan.payment_box.notifications.loan_paid");
      }

      this.loading = true;
      const { data } = await put(
        endpoint,
        {
          platform_tip: tip,
        },
        { cleanupCallback: () => (this.loading = false), notifications: { onSuccess, action } }
      );

      // Load user if needed
      if (!this.userHasSufficientBalance || this.loan.status === "validated") {
        this.$store.dispatch("loadUser");
      }
      this.$emit("input", data);
      this.$emit("estimate"); // reset estimation, since updated loan has fresh invoices
      this.$emit("jump", sections.loan);
    },
    tipAmountChanged(newAmount) {
      if (this.platformTip === newAmount) {
        return;
      }

      this.platformTip = normalizeCurrency(newAmount ?? 0);
      this.debouncedEstimatePrice();
    },
    async estimatePrice() {
      this.$emit("estimate", this.platformTip);
    },
    async handlePaid() {
      await this.onPlatformTipSaved(this.platformTip);
    },
    async handlePaymentButtonClick(closeEdit = null) {
      // await tick, since currency input only changes value on focus lost.
      await this.$nextTick();

      if (this.estimating) {
        return;
      }

      if (!this.loan.id) {
        closeEdit && closeEdit();
        return;
      }

      if (!this.userHasSufficientBalance) {
        this.$refs["provision-modal"].show();
      } else {
        await this.onPlatformTipSaved(this.platformTip);
        if (closeEdit && !this.initiallyEditing) {
          closeEdit();
        }
      }
    },
    reset() {
      this.platformTip = normalizeCurrency(this.loan.platform_tip);
      this.$emit("estimate");
    },
    maybeSetPlatformTipToDesiredValue() {
      if (
        this.loan.platform_tip === null &&
        this.initiallyEditing &&
        this.platformTip < this.desiredContribution
      ) {
        this.platformTip = normalizeCurrency(this.desiredContribution);
        this.estimatePrice();
      }
    },
  },
};
</script>

<template>
  <loan-page-editable-box class="loan-payment-box" :initially-editing="initiallyEditing">
    <template #default="{ edit }">
      <div v-if="isBorrower || isLoanAdmin">
        <tiny-invoice
          v-if="summarizedBorrowerInvoiceItems && summarizedBorrowerInvoiceItems.length > 0"
          :invoice="{ items: summarizedBorrowerInvoiceItems }"
          :loading="isInvoiceUpdating && isBorrower"
          :styled="false"
        />
        <div v-else>{{ $t("components.loan.payment_box.free_loan") }}</div>
      </div>
      <div v-else-if="isCoownerOrOwner">
        <tiny-invoice
          v-if="summarizedOwnerInvoice"
          :invoice="groupedSummarizedOwnerInvoice"
          :styled="false"
        />

        <tiny-invoice
          v-if="sharedInvoice"
          class="mt-3"
          :invoice="groupedSharedInvoice"
          :styled="false"
        />
      </div>

      <provision-modal
        v-if="isBorrower && !userHasSufficientBalance && loanIsInProcess"
        ref="provision-modal"
        :price="actualPrice"
        :loading="loading || estimating"
        :action-name="actionLabel"
        reload-user
        minimum-default
        @complete="
          () => {
            $bvModal.hide('payment-provision-modal');
            handlePaid();
          }
        "
      />

      <template v-if="isBorrower && loanIsInProcess">
        <icon-button v-if="!isExemptFromContributions" block role="edit" :onclick="edit">{{
          $t("components.loan.payment_box.modify_contribution")
        }}</icon-button>
        <template
          v-else-if="
            !userHasSufficientBalance || loan.status === 'accepted' || loan.status === 'validated'
          "
        >
          <icon-button
            variant="primary"
            block
            :disabled="disabled || isInvoiceUpdating || isChangingDistance || isChangingDuration"
            :loading="loading"
            :onclick="handlePaymentButtonClick"
          >
            {{ paymentButtonLabel | capitalize }}
          </icon-button>
          <b-alert v-if="isChangingDistance || isChangingDuration" show variant="warning">
            <strong>{{ $t("components.loan.payment_box.warning") }}</strong>
            {{ $t("components.loan.payment_box.estimation_includes_changes") }}
            (<icon-button
              v-if="isChangingDuration"
              size="inline"
              variant="ghost-secondary"
              @click="$emit('jump', sections.dates)"
            >
              {{ $t("components.loan.payment_box.dates") }}
            </icon-button>
            <span v-if="isChangingDuration && isChangingDistance">
              {{ $t("components.loan.payment_box.and") }}
            </span>
            <icon-button
              v-if="isChangingDistance"
              size="inline"
              variant="ghost-secondary"
              @click="$emit('jump', sections.info)"
            >
              {{ $t("components.loan.payment_box.distance_traveled") }}</icon-button
            >).
            {{ $t("components.loan.payment_box.save_or_cancel_changes") }}
          </b-alert>
        </template>
      </template>
    </template>

    <template #form="{ close }">
      <donation-slider
        ref="donationSlider"
        :title="$t('components.loan.payment_box.donation_slider.title')"
        image="/Foire_des_Possibles2023.jpg"
        :disabled="loading"
        :amount="platformTip"
        :target="desiredContribution"
        :subscription-available="loan.subscription_available"
        :contribution-required="loan.applicable_amount_types.contributions === 'required'"
        :pricing-loanable-type="loan.loanable.pricing_loanable_type"
        :community-id="loan.community.id"
        @amountChanged="tipAmountChanged"
      >
        <p>{{ $t("components.loan.payment_box.donation_slider.description_1") }}</p>
        <p class="mb-1">{{ $t("components.loan.payment_box.donation_slider.description_2") }}</p>
      </donation-slider>

      <div class="my-3">
        <tiny-invoice
          :invoice="{ items: summarizedBorrowerInvoiceItems }"
          :loading="isInvoiceUpdating && isBorrower"
          :styled="false"
        />
      </div>

      <provision-modal
        v-if="isBorrower && !userHasSufficientBalance && loanIsInProcess"
        ref="provision-modal"
        :price="actualPrice"
        :loading="loading"
        :action-name="actionLabel"
        :reload-user="false"
        minimum-default
        @complete="
          () => {
            $bvModal.hide('payment-provision-modal');
            handlePaid();
          }
        "
      />

      <b-alert
        v-if="(isChangingDuration || isChangingDistance) && loan.id"
        show
        variant="warning"
        class="mb-3"
      >
        <strong>{{ $t("components.loan.payment_box.warning") }}</strong>
        {{ $t("components.loan.payment_box.estimation_includes_changes") }}
        (<icon-button
          v-if="isChangingDuration"
          size="inline"
          variant="ghost-secondary"
          @click="$emit('jump', sections.dates)"
        >
          {{ $t("components.loan.payment_box.dates") }}
        </icon-button>
        <span v-if="isChangingDuration && isChangingDistance">
          {{ $t("components.loan.payment_box.and") }}
        </span>
        <icon-button
          v-if="isChangingDistance"
          size="inline"
          variant="ghost-secondary"
          @click="$emit('jump', sections.info)"
        >
          {{ $t("components.loan.payment_box.distance_traveled") }}</icon-button
        >).
        <span v-if="wouldPayOrPrepayWithChangedInfo">
          {{ $t("components.loan.payment_box.save_or_cancel_changes") }}
        </span>
      </b-alert>

      <div>
        <slot name="complete-button">
          <icon-button
            v-if="!wouldSaveUnchangedTip || !userHasSufficientBalance"
            variant="primary"
            block
            :disabled="disabled || isInvoiceUpdating || wouldPayOrPrepayWithChangedInfo"
            :loading="loading"
            :onclick="async () => handlePaymentButtonClick(close)"
          >
            {{ paymentButtonLabel | capitalize }}
          </icon-button>
        </slot>
        <icon-button
          v-if="!initiallyEditing"
          class="mt-2"
          role="cancel"
          :disabled="loading"
          block
          :onclick="
            () => {
              close();
              reset();
            }
          "
          >{{ $t("forms.cancel") | capitalize }}</icon-button
        >
      </div>
    </template>
  </loan-page-editable-box>
</template>

<style lang="scss">
.loan-payment-box {
  .tiny-invoice {
    margin-bottom: 0;

    .invoice-contribution th,
    .invoice-contribution td {
      color: $locomotion-light-green;
    }
  }

  .invoice-total {
    color: $primary;
    font-size: 1.25rem;
    th,
    td {
      border-top: 1px solid $grey;
    }
  }
}
</style>
